"use client";

import { ReactNode, useEffect, useRef } from "react";
import { cn } from "@/utils/cn";

type ConfirmExitModalProps = {
  isOpen: boolean;
  title: string;
  description: string;
  details?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isBusy?: boolean;
  confirmIsPrimary?: boolean;
  className?: string;
};

const focusableSelector =
  "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

export function ConfirmExitModal({
  isOpen,
  title,
  description,
  details,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  isBusy = false,
  confirmIsPrimary = false,
  className,
}: ConfirmExitModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const dialog = dialogRef.current;
    if (dialog) {
      const initialFocus = dialog.querySelector<HTMLElement>("[data-focus='safe']");
      initialFocus?.focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
        return;
      }

      if (event.key !== "Tab") return;
      const node = dialogRef.current;
      if (!node) return;

      const focusable = Array.from(node.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (element) => !element.hasAttribute("disabled") && element.tabIndex !== -1
      );

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center bg-[#1f2b27]/35 p-0 sm:items-center sm:p-4">
      <div
        role="presentation"
        className="absolute inset-0"
        onClick={() => !isBusy && onCancel()}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-exit-title"
        aria-describedby="confirm-exit-description"
        className={cn(
          "relative z-10 w-full max-w-md rounded-t-3xl border border-[#d8cab2] bg-[#fffaf1] p-5 shadow-[0_-8px_24px_rgba(20,31,27,0.15)] sm:rounded-2xl sm:p-6 sm:shadow-[0_18px_36px_rgba(20,31,27,0.2)]",
          className
        )}
      >
        <h2 id="confirm-exit-title" className="text-lg font-semibold text-[#24322e]">
          {title}
        </h2>
        <p id="confirm-exit-description" className="mt-2 text-sm text-[#50625c]">
          {description}
        </p>
        {details ? <div className="mt-3 text-sm text-[#41564f]">{details}</div> : null}

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onConfirm}
            disabled={isBusy}
            className={cn(
              "inline-flex min-h-11 w-full items-center justify-center rounded-xl px-4 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60",
              confirmIsPrimary
                ? "border border-[#0f766e]/30 bg-[#0f766e] font-semibold text-white hover:bg-[#0d695f]"
                : "border border-[#cbbda3] bg-white font-medium text-[#41564f] hover:border-[#b69e77]"
            )}
          >
            {confirmLabel}
          </button>
          <button
            type="button"
            data-focus="safe"
            onClick={onCancel}
            disabled={isBusy}
            className={cn(
              "inline-flex min-h-11 w-full items-center justify-center rounded-xl px-4 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-70",
              confirmIsPrimary
                ? "border border-[#cbbda3] bg-white font-medium text-[#41564f] hover:border-[#b69e77]"
                : "border border-[#0f766e]/30 bg-[#0f766e] font-semibold text-white hover:bg-[#0d695f]"
            )}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
